import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, CircularProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import { chatAPI } from '../../services/api';
import {
  pushSupported, enablePush, syncPushSubscription, getPushStatus, pushDiagnostics, isIos, isStandalone,
} from '../../services/push';
import { blockedHelp } from './PushBanner';

const ICONS = {
  ok: <CheckCircleIcon color="success" />,
  fail: <ErrorIcon color="error" />,
  info: <InfoIcon color="info" />,
};

/**
 * Prueba de notificaciones: comprueba uno a uno los pasos por los que tiene que
 * pasar un aviso (soporte, permiso, suscripcion de este movil, envio desde el
 * servidor) y dice en cual se rompe. Sin esto, "no me llegan" no tiene
 * diagnostico: cada paso falla por un motivo distinto y se arregla en un sitio
 * distinto.
 */
// Espera el aviso del service worker de que la notificacion de prueba ha llegado
// (y si se ha podido mostrar). null si no llega a tiempo.
function waitForTestResult(ms) {
  return new Promise((resolve) => {
    const onMessage = (event) => {
      if (event.data?.type !== 'push-test-result') return;
      navigator.serviceWorker.removeEventListener('message', onMessage);
      clearTimeout(timer);
      resolve(event.data);
    };
    const timer = setTimeout(() => {
      navigator.serviceWorker.removeEventListener('message', onMessage);
      resolve(null);
    }, ms);
    navigator.serviceWorker.addEventListener('message', onMessage);
  });
}

export default function PushTestDialog({ open, onClose }) {
  const [steps, setSteps] = useState([]);
  const [running, setRunning] = useState(false);
  const [needsActivation, setNeedsActivation] = useState(false);
  const [diag, setDiag] = useState([]);
  const [activationError, setActivationError] = useState('');

  const run = useCallback(async (lastError = '') => {
    const out = [];
    const push = (status, label, detail) => { out.push({ status, label, detail }); setSteps([...out]); };
    setRunning(true);
    setNeedsActivation(false);
    setSteps([]);
    try {
      if (isIos() && !isStandalone()) {
        push('fail', 'App instalada',
          'En iPhone las notificaciones solo funcionan con la app instalada. Pulsa Compartir > '
          + 'Anadir a pantalla de inicio y abrela desde ese icono.');
        return;
      }
      if (!pushSupported()) {
        push('fail', 'Este navegador admite notificaciones',
          'Este navegador o version no permite notificaciones push.');
        return;
      }
      push('ok', 'Este navegador admite notificaciones');

      // Si ya hay permiso, se registra este movil antes de mirar el estado
      await syncPushSubscription().catch(() => {});
      const status = await getPushStatus();
      setDiag(await pushDiagnostics());

      if (status.state === 'denied') {
        push('fail', 'Permiso de notificaciones', `Esta bloqueado. Activalo en: ${blockedHelp()}`);
        return;
      }
      if (status.state !== 'active') {
        push('fail', 'Notificaciones activadas en este movil',
          (lastError ? `No se ha podido activar: ${lastError}. ` : 'Todavia no estan activadas. ')
          + 'Pulsa Activar.');
        setNeedsActivation(true);
        return;
      }
      push('ok', 'Notificaciones activadas en este movil');

      // Se escucha antes de pedir el envio: el aviso puede llegar antes de que vuelva la respuesta
      const arrivalPromise = waitForTestResult(20000);
      const res = await chatAPI.pushTest();
      const { subscriptions, results } = res.data;
      if (!subscriptions) {
        push('fail', 'El servidor conoce este movil', 'El servidor no tiene ningun movil registrado para ti.');
        return;
      }
      const failed = results.filter((r) => !r.ok);
      if (failed.length === results.length) {
        push('fail', 'Envio desde el servidor', failed.map((r) => r.detail).join(' / '));
        return;
      }
      push('ok', 'Envio desde el servidor', `${results.length - failed.length} de ${results.length} aceptados.`);

      // Ultimo paso, el que de verdad importa: que el aviso llegue a ESTE movil y
      // el sistema deje mostrarlo. Lo confirma el service worker (sw.js).
      const arrival = await arrivalPromise;
      if (!arrival) {
        push('fail', 'Llega a este movil',
          'El servidor lo ha enviado pero no ha llegado en 20 segundos. Puede ser el ahorro de '
          + 'bateria del telefono o que el movil este sin conexion; vuelve a probar.');
      } else if (!arrival.ok) {
        push('fail', 'Llega a este movil, pero el sistema no deja mostrarlo', `${arrival.error}. ${blockedHelp()}`);
      } else {
        push('ok', 'Llega a este movil y se muestra',
          'Tiene que estar en la barra de notificaciones como "Prueba de notificaciones".');
      }
    } catch (err) {
      push('fail', 'Prueba', err.response?.data?.detail || err.message || 'Error inesperado');
    } finally {
      setRunning(false);
    }
  }, []);

  useEffect(() => { if (open) run(); }, [open, run]);

  const activate = async () => {
    setRunning(true);
    const result = await enablePush();
    const error = result.ok ? '' : result.error;
    setActivationError(error);
    run(error);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Probar notificaciones</DialogTitle>
      <DialogContent>
        {steps.map((step, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 1.25, mb: 1.5, alignItems: 'flex-start' }}>
            {ICONS[step.status]}
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600}>{step.label}</Typography>
              {step.detail && (
                <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                  {step.detail}
                </Typography>
              )}
            </Box>
          </Box>
        ))}
        {running && <Box sx={{ textAlign: 'center', p: 1 }}><CircularProgress size={24} /></Box>}
        {diag.length > 0 && (
          <Box sx={{ mt: 1, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="caption" fontWeight={600} display="block">Datos tecnicos</Typography>
            {diag.map((line, i) => (
              <Typography key={i} variant="caption" display="block"
                sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{line}</Typography>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {needsActivation && <Button variant="contained" onClick={activate}>Activar notificaciones</Button>}
        <Button onClick={() => run(activationError)} disabled={running}>Repetir</Button>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}
